import React, { useEffect, useRef, useState } from 'react';
import Table from '../../../components/common/Table';
import Button from '../../../components/common/Button';
import Drawer from '../../../components/common/Drawer';
import Badge from '../../../components/common/Badge';
import { useNetcashBatchApi } from '../../../hooks/useNetcashBatchApi';
import { confirmAction, confirmDelete, showAlert } from '../../../utils/sweetAlertUtils';
import { NetcashBatchDTO, NetcashBatchLineItemDTO } from '../../../services/apiClient';
import { convertToUserTime } from '../../../utils/dateUtils/convertToUserTime';
import NetcashPushDrawer from '../../../components/drawer/NetcashPushDrawer';
import { formatActionDate } from '../../../utils/netcashDates';
import { getApiErrorMessage } from '../../../utils/apiErrorUtils';
import { Ban, EllipsisVertical, Trash } from 'lucide-react';

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const NETCASH_PUSH_POLL_INTERVAL_MS = 3000;
// Netcash normally reports within a minute; stop watching after this rather than poll forever.
const NETCASH_RESULT_MAX_WAIT_MS = 10 * 60 * 1000;

const resultBadges: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'info' | 'default' }> = {
  AwaitingReport: { label: 'Awaiting Netcash', variant: 'info' },
  Accepted: { label: 'Accepted', variant: 'success' },
  AcceptedWithRejections: { label: 'Accepted with rejections', variant: 'warning' },
  Failed: { label: 'Failed', variant: 'error' },
  ReportTimedOut: { label: 'No report received', variant: 'warning' },
  Unknown: { label: 'Not tracked', variant: 'default' },
  Cancelled: { label: 'Cancelled on Netcash', variant: 'default' },
};

const exclusionReasonLabels: Record<string, string> = {
  InvoiceNoLongerEligible: 'Invoice no longer an eligible unpaid sales order (paid, finalized or deleted)',
  NoOutstandingAmount: 'No outstanding amount',
  MissingIdPassportNumber: 'Member has no ID/Passport number',
  NoBankingDetails: 'No active mandate and no Netcash masterfile match',
  Unknown: 'Dropped at push time (recorded before reasons were tracked)',
};

const formatMoney = (value?: number | null) => `R ${(value ?? 0).toFixed(2)}`;

type StatusFilter = 'All' | 'Accepted' | 'Rejected' | 'Excluded' | 'Included' | 'Released' | 'Pending';

const statusLabels: Record<string, string> = {
  Included: 'Sent',
};

const NetcashBatchesPanel: React.FC = () => {
  const { getDrafts, getHistory, getById, removeLineItem, push, deleteDraft, markCancelled, releaseLineItem, isLoading } = useNetcashBatchApi();
  const [draftMenuId, setDraftMenuId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<NetcashBatchDTO[]>([]);
  const [history, setHistory] = useState<NetcashBatchDTO[]>([]);
  const [pushTarget, setPushTarget] = useState<NetcashBatchDTO | null>(null);
  const [isQueuingPush, setIsQueuingPush] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  // The draft we're waiting on, so we can tell a finished push (it leaves the drafts list) apart
  // from "there's just no draft" once it drops out of the response.
  const watchedPushBatchId = useRef<string | null>(null);
  // Set once the file has been uploaded: the batch is not finished until Netcash has reported
  // which debtors it accepted (which can involve a resubmission), so we keep following it.
  const [awaitingBatchId, setAwaitingBatchId] = useState<string | null>(null);

  const fetchDrafts = async () => {
    try {
      const response = await getDrafts();
      setDrafts(response?.data ?? []);
    } catch (error) {
      console.error('Error fetching draft Netcash batches:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await getHistory();
      const batches = response?.data ?? [];
      setHistory(batches);

      const waiting = batches.find((b) => b.resultStatus === 'AwaitingReport');
      if (waiting?.id) {
        setAwaitingBatchId((prev) => prev ?? waiting.id ?? null);
      }
    } catch (error) {
      console.error('Error fetching Netcash batch history:', error);
    }
  };

  useEffect(() => {
    fetchDrafts();
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The push itself runs in the background, so once it's queued we poll the drafts until
  // isPushing flips back off (failed) or the batch leaves the drafts list (uploaded).
  const anyPushing = drafts.some((d) => d.isPushing);
  useEffect(() => {
    if (!anyPushing) return;

    const interval = setInterval(() => {
      fetchDrafts();
    }, NETCASH_PUSH_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyPushing]);

  useEffect(() => {
    const watchedId = watchedPushBatchId.current;
    if (!watchedId) return;

    const watched = drafts.find((d) => d.id === watchedId);

    if (watched && watched.pushErrorMessage && !watched.isPushing) {
      watchedPushBatchId.current = null;
      showAlert({
        title: 'Netcash Push Failed',
        text: watched.pushErrorMessage,
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }

    // The batch left the drafts, so the file has been uploaded - but that is not the outcome yet.
    // Keep following it until Netcash reports which debtors were accepted.
    if (!watched) {
      setAwaitingBatchId(watchedId);
      watchedPushBatchId.current = null;
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drafts]);

  const announceResult = (batch: NetcashBatchDTO) => {
    const items = batch.lineItems ?? [];
    const accepted = items.filter((li) => li.status === 'Accepted').length;
    const rejected = items.filter((li) => li.status === 'Rejected').length;
    const authorise = 'Remember to authorise/release it in the Netcash portal.';

    switch (batch.resultStatus) {
      case 'Accepted':
        return showAlert({
          title: 'Batch Created in Netcash',
          text: `All ${accepted} debtor(s) were accepted. ${authorise}`,
          icon: 'success',
          confirmButtonText: 'OK',
        });
      case 'AcceptedWithRejections':
        return showAlert({
          title: 'Batch Created - Some Debtors Rejected',
          text: `${accepted} debtor(s) were accepted and the batch was created. ${rejected} debtor(s) were rejected by Netcash and left out - open View Details on this batch to see who and why. ${authorise}`,
          icon: 'warning',
          confirmButtonText: 'OK',
        });
      case 'Failed':
        return showAlert({
          title: 'Netcash Did Not Create the Batch',
          text: batch.pushErrorMessage || 'Netcash refused the upload file. Open View Details on this batch for the reasons.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      case 'ReportTimedOut':
        return showAlert({
          title: 'No Report From Netcash Yet',
          text: 'The file was uploaded, but Netcash has not reported back which debtors were accepted. Check the batch in the Netcash portal.',
          icon: 'warning',
          confirmButtonText: 'OK',
        });
      default:
        return undefined;
    }
  };

  // Follow an uploaded batch until Netcash has answered - the first upload can be refused and
  // resubmitted without the rejected debtors, so "uploaded" is not the same as "done".
  useEffect(() => {
    if (!awaitingBatchId) return;

    const startedAt = Date.now();
    let cancelled = false;

    const interval = setInterval(async () => {
      if (Date.now() - startedAt > NETCASH_RESULT_MAX_WAIT_MS) {
        setAwaitingBatchId(null);
        return;
      }

      try {
        const response = await getById(awaitingBatchId);
        const batch = response?.data;
        if (cancelled || !batch || batch.resultStatus === 'AwaitingReport') return;

        setAwaitingBatchId(null);
        await fetchHistory();
        await announceResult(batch);
      } catch (error) {
        console.error('Error checking Netcash batch result:', error);
      }
    }, NETCASH_PUSH_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingBatchId]);

  const handleRemoveLineItem = async (row: NetcashBatchLineItemDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await removeLineItem(row.id || '');
      await fetchDrafts();
    } catch (error) {
      console.error('Error removing line item:', error);
    }
  };

  const handleDeleteDraft = async (draft: NetcashBatchDTO) => {
    setDraftMenuId(null);
    const count = draft.totalCount ?? 0;
    const confirmed = await confirmDelete(
      count > 0
        ? `Delete the ${getPeriodLabel(draft)} draft? Its ${count} invoice(s) will be freed so they can be added to another batch.`
        : `Delete the empty ${getPeriodLabel(draft)} draft?`
    );
    if (!confirmed) return;

    try {
      await deleteDraft(draft.id || '');
      await fetchDrafts();
    } catch (error: any) {
      await showAlert({
        title: 'Error',
        text: getApiErrorMessage(error, 'Failed to delete the draft batch.'),
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  const doubleDebitWarning =
    'Only do this if you have cancelled it in the Netcash portal - otherwise Netcash will still collect and the member(s) will be debited twice.';

  const handleMarkCancelled = async (batch: NetcashBatchDTO) => {
    const confirmed = await confirmAction({
      title: 'Cancelled on Netcash?',
      text: `Mark the ${getPeriodLabel(batch)} batch as cancelled? Its sent members will be released so they can be added to a new batch. ${doubleDebitWarning}`,
      icon: 'warning',
      confirmButtonText: 'Yes, it was cancelled',
      cancelButtonText: 'No',
    });
    if (!confirmed) return;

    try {
      await markCancelled(batch.id || '');
      await fetchHistory();
    } catch (error: any) {
      await showAlert({
        title: 'Error',
        text: getApiErrorMessage(error, 'Failed to mark the batch as cancelled.'),
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  const handleReleaseLineItem = async (row: NetcashBatchLineItemDTO) => {
    const confirmed = await confirmAction({
      title: 'Release member?',
      text: `Release ${row.memberName || 'this member'} so they can be added to a new batch? ${doubleDebitWarning}`,
      icon: 'warning',
      confirmButtonText: 'Yes, release',
      cancelButtonText: 'No',
    });
    if (!confirmed) return;

    try {
      await releaseLineItem(row.id || '');
      await fetchHistory();
    } catch (error: any) {
      await showAlert({
        title: 'Error',
        text: getApiErrorMessage(error, 'Failed to release the member.'),
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  const isReleasable = (li: NetcashBatchLineItemDTO) => li.status === 'Accepted' || li.status === 'Included';

  const handlePush = async (actionDate: string) => {
    if (!pushTarget?.id) return;
    const target = pushTarget;

    setIsQueuingPush(true);
    try {
      const response = await push(target.id!, actionDate);
      watchedPushBatchId.current = target.id!;
      setPushTarget(null);
      const updated = response?.data;
      setDrafts((prev) => prev.map((d) => (d.id === target.id ? updated ?? d : d)));
      await showAlert({
        title: 'Push Queued',
        text: `The batch is being uploaded to Netcash for collection on ${formatActionDate(actionDate)} - this page will update automatically once it finishes.`,
        icon: 'success',
        confirmButtonText: 'OK',
      });
    } catch (error: any) {
      console.error('Error queuing Netcash batch for push:', error);
      await showAlert({
        title: 'Error',
        text: getApiErrorMessage(error, 'Failed to queue Netcash batch for push. Please try again.'),
        icon: 'error',
        confirmButtonText: 'OK',
      });
    } finally {
      setIsQueuingPush(false);
    }
  };

  const getPeriodLabel = (batch: NetcashBatchDTO) => `${monthNames[(batch.month ?? 1) - 1] || ''} ${batch.year ?? ''}`;

  const selectedBatch = history.find((b) => b.id === selectedBatchId) ?? null;
  const selectedLineItems = selectedBatch?.lineItems ?? [];
  const sentItems = selectedLineItems.filter((li) => li.status === 'Included' || li.status === 'Accepted');
  const acceptedItems = selectedLineItems.filter((li) => li.status === 'Accepted');
  const rejectedItems = selectedLineItems.filter((li) => li.status === 'Rejected');
  const excludedItems = selectedLineItems.filter((li) => li.status === 'Excluded');
  const filteredLineItems = selectedLineItems.filter((li) => statusFilter === 'All' || li.status === statusFilter);
  const reportMismatch =
    selectedBatch?.netcashReportedAmount != null &&
    selectedBatch?.pushedTotalAmount != null &&
    selectedBatch.netcashReportedAmount !== selectedBatch.pushedTotalAmount;

  const renderTile = (label: string, value: string, tone: 'default' | 'warn' | 'good' = 'default') => (
    <div key={label} className="rounded-lg border border-gray-200 dark:border-slate-700 p-3">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div
        className={`text-lg font-semibold ${
          tone === 'warn' ? 'text-red-600 dark:text-red-400' : tone === 'good' ? 'text-green-600 dark:text-green-400' : 'text-slate-800 dark:text-slate-100'
        }`}
      >
        {value}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Draft Batches</h3>

        {awaitingBatchId && !anyPushing && (
          <p className="text-sm text-blue-600 dark:text-blue-400">
            A batch has been uploaded to Netcash. Waiting for Netcash to confirm which debtors were accepted - this can take a minute, and
            the batch is resubmitted automatically if any debtors are rejected. This page will update when it is done.
          </p>
        )}

        {drafts.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            No draft batches. Add invoices from the Debit Order tab on an Invoice Batch to start one - you can have several drafts at once,
            including more than one for the same month.
          </p>
        ) : (
          drafts.map((draft) => (
            <div key={draft.id} className="rounded-lg border border-gray-200 dark:border-slate-700 p-4">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <h4 className="font-semibold text-slate-800 dark:text-slate-100">{getPeriodLabel(draft)} draft</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {draft.totalCount} invoice(s) | R{(draft.totalAmount ?? 0).toFixed(2)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => setPushTarget(draft)}
                    isLoading={!!draft.isPushing}
                    disabled={isQueuingPush || !!draft.isPushing || !!awaitingBatchId || (draft.totalCount ?? 0) === 0}
                  >
                    {draft.isPushing ? 'Pushing to Netcash…' : 'Push to Netcash'}
                  </Button>
                  {!draft.isPushing && (
                    <div className="relative">
                      <button
                        type="button"
                        aria-label="Draft actions"
                        onClick={() => setDraftMenuId((prev) => (prev === draft.id ? null : draft.id ?? null))}
                        className="text-gray-700 bg-gray-200 p-2 rounded hover:bg-gray-300 cursor-pointer"
                      >
                        <EllipsisVertical className="h-4 w-4" />
                      </button>
                      {draftMenuId === draft.id && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setDraftMenuId(null)} />
                          <div className="absolute right-0 mt-2 min-w-[180px] whitespace-nowrap bg-white shadow-lg z-50 rounded-md overflow-hidden">
                            <button
                              type="button"
                              onClick={() => handleDeleteDraft(draft)}
                              className="flex items-center px-4 py-2 hover:bg-gray-100 w-full text-left cursor-pointer text-red-600"
                            >
                              <Trash className="w-4 h-4 mr-2" />
                              Delete draft
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {draft.isPushing && (
                <p className="text-sm text-blue-600 dark:text-blue-400 mb-2">
                  This batch is being pushed to Netcash
                  {draft.actionDate ? ` for collection on ${formatActionDate(draft.actionDate)}` : ''} in the background. This page will update
                  automatically once it finishes.
                </p>
              )}

              {(draft.lineItems?.length ?? 0) === 0 ? (
                <p className="text-sm text-gray-600 dark:text-gray-400">No invoices in this draft yet.</p>
              ) : (
                <Table
                  columns={[
                    { label: 'Member', key: 'memberName', render: (row: NetcashBatchLineItemDTO) => <span>{row.memberName || 'Unknown'}</span> },
                    { label: 'ID/Passport', key: 'memberIdPassportNumber' },
                    { label: 'Sales Order', key: 'salesOrderNumber' },
                    {
                      label: 'Amount',
                      key: 'amountAtAdd',
                      render: (row: NetcashBatchLineItemDTO) => <span>R {row.amountAtAdd?.toFixed(2)}</span>,
                    },
                  ]}
                  data={draft.lineItems || []}
                  actions={{
                    extra: (row: NetcashBatchLineItemDTO) => [
                      { label: 'Remove', onClick: () => handleRemoveLineItem(row) },
                    ],
                  }}
                  isLoading={isLoading}
                />
              )}
            </div>
          ))
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
          Pushed Batch History
        </h3>
        {history.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">No Netcash batches have been pushed yet.</p>
        ) : (
          <Table
            columns={[
              { label: 'Period', key: 'period', render: (row: NetcashBatchDTO) => <span>{getPeriodLabel(row)}</span> },
              { label: 'Collection Date', key: 'actionDate', render: (row: NetcashBatchDTO) => <span>{formatActionDate(row.actionDate)}</span> },
              {
                label: 'Pushed',
                key: 'pushedUtc',
                format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
              },
              {
                label: 'Result',
                key: 'resultStatus',
                render: (row: NetcashBatchDTO) => {
                  const badge = resultBadges[row.resultStatus ?? ''] ?? { label: row.resultStatus ?? '-', variant: 'default' as const };
                  return <Badge label={badge.label} variant={badge.variant} />;
                },
              },
              { label: 'File Token', key: 'fileToken' },
              {
                label: 'Sent',
                key: 'pushedMemberCount',
                render: (row: NetcashBatchDTO) => (
                  <span>
                    {row.pushedMemberCount ?? '-'} member(s) / {formatMoney(row.pushedTotalAmount ?? row.totalAmount)}
                  </span>
                ),
              },
              {
                label: 'Netcash Reported',
                key: 'netcashReportedAmount',
                render: (row: NetcashBatchDTO) => {
                  if (row.netcashReportedAmount == null) return <span>-</span>;
                  const mismatch = row.pushedTotalAmount != null && row.netcashReportedAmount !== row.pushedTotalAmount;
                  return (
                    <span className={mismatch ? 'text-red-600 dark:text-red-400 font-semibold' : ''}>
                      {formatMoney(row.netcashReportedAmount)}
                    </span>
                  );
                },
              },
            ]}
            data={history}
            isLoading={isLoading}
            actions={{
              extra: (row: NetcashBatchDTO) => [
                {
                  label: 'View Details',
                  onClick: () => {
                    setSelectedBatchId(row.id ?? null);
                    setStatusFilter('All');
                  },
                },
                ...(row.resultStatus !== 'Cancelled' && (row.lineItems ?? []).some(isReleasable)
                  ? [
                      {
                        label: 'Mark as cancelled on Netcash',
                        onClick: () => handleMarkCancelled(row),
                        icon: <Ban className="w-4 h-4" />,
                        className: 'text-red-600',
                      },
                    ]
                  : []),
              ],
            }}
          />
        )}
      </div>

      <NetcashPushDrawer
        isOpen={!!pushTarget}
        onClose={() => setPushTarget(null)}
        batch={pushTarget}
        isSubmitting={isQueuingPush}
        onConfirm={handlePush}
      />

      <Drawer
        isOpen={!!selectedBatch}
        onClose={() => setSelectedBatchId(null)}
        title={selectedBatch ? `Netcash Batch - ${getPeriodLabel(selectedBatch)}` : ''}
        size="large"
      >
        {selectedBatch && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {renderTile('Invoices in batch', String(selectedLineItems.length))}
            {renderTile('Collection date', formatActionDate(selectedBatch.actionDate))}
            {renderTile('Excluded before sending', String(excludedItems.length), excludedItems.length > 0 ? 'warn' : 'default')}
            {renderTile('Sent to Netcash', `${sentItems.length} (${formatMoney(selectedBatch.pushedTotalAmount)})`)}
            {renderTile('Accepted by Netcash', String(acceptedItems.length), acceptedItems.length > 0 ? 'good' : 'default')}
            {renderTile('Rejected by Netcash', String(rejectedItems.length), rejectedItems.length > 0 ? 'warn' : 'default')}
            {renderTile(
              'Netcash report total',
              selectedBatch.netcashReportedAmount != null ? formatMoney(selectedBatch.netcashReportedAmount) : 'Not yet received',
              reportMismatch ? 'warn' : 'default'
            )}
          </div>

          {(selectedBatch.pushAttempt ?? 1) > 1 && (
            <p className="text-sm text-amber-700 dark:text-amber-400 mb-3">
              Netcash refused the first upload because of the rejected debtors below, so the batch was resubmitted without them
              ({selectedBatch.pushAttempt} uploads in total). The file token and totals shown are for the latest upload.
            </p>
          )}

          {selectedBatch.pushErrorMessage && (
            <p className="text-sm text-red-600 dark:text-red-400 mb-3">{selectedBatch.pushErrorMessage}</p>
          )}

          {reportMismatch && (
            <p className="text-sm text-red-600 dark:text-red-400 mb-3">
              Netcash's report total is {formatMoney(selectedBatch.netcashReportedAmount)} but we sent {formatMoney(selectedBatch.pushedTotalAmount)} (difference{' '}
              {formatMoney((selectedBatch.pushedTotalAmount ?? 0) - (selectedBatch.netcashReportedAmount ?? 0))}). Netcash's report does not say which debtors were dropped -
              check the batch in the Netcash portal.
            </p>
          )}

          <div className="flex gap-2 mb-3">
            {(['All', 'Accepted', 'Rejected', 'Excluded', 'Included', 'Released', 'Pending'] as StatusFilter[]).map((f) => (
              <Button
                key={f}
                type="button"
                variant={statusFilter === f ? 'primary' : 'secondary'}
                onClick={() => setStatusFilter(f)}
              >
                {statusLabels[f] ?? f}
              </Button>
            ))}
          </div>

          <Table
            columns={[
              { label: 'Member', key: 'memberName', render: (row: NetcashBatchLineItemDTO) => <span>{row.memberName || 'Unknown'}</span> },
              { label: 'ID/Passport', key: 'memberIdPassportNumber' },
              { label: 'Sales Order', key: 'salesOrderNumber' },
              {
                label: 'Amount',
                key: 'amountAtAdd',
                render: (row: NetcashBatchLineItemDTO) => <span>{formatMoney(row.amountAtAdd)}</span>,
              },
              {
                label: 'Status',
                key: 'status',
                render: (row: NetcashBatchLineItemDTO) => (
                  <Badge
                    label={statusLabels[row.status ?? ''] ?? row.status ?? 'Unknown'}
                    variant={
                      row.status === 'Accepted'
                        ? 'success'
                        : row.status === 'Rejected' || row.status === 'Excluded'
                        ? 'error'
                        : row.status === 'Included'
                        ? 'info'
                        : row.status === 'Released'
                        ? 'default'
                        : 'warning'
                    }
                  />
                ),
              },
              {
                label: 'Detail',
                key: 'exclusionReason',
                render: (row: NetcashBatchLineItemDTO) => (
                  <span>
                    {row.status === 'Excluded'
                      ? exclusionReasonLabels[row.exclusionReason ?? ''] ?? row.exclusionReason
                      : row.status === 'Rejected'
                      ? `Netcash: ${row.netcashResultMessage ?? 'rejected'}`
                      : row.bankingSource
                      ? `Banking via ${row.bankingSource}`
                      : ''}
                  </span>
                ),
              },
            ]}
            data={filteredLineItems}
            isLoading={isLoading}
            actions={{
              extra: (row: NetcashBatchLineItemDTO) =>
                isReleasable(row) ? [{ label: 'Release member', onClick: () => handleReleaseLineItem(row) }] : [],
            }}
          />
        </div>
        )}
      </Drawer>
    </div>
  );
};

export default NetcashBatchesPanel;
